import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import DoctorShell from "@/components/DoctorShell";
import { inr } from "@/lib/constants";

/**
 * Cashfree return_url landing page.
 *
 * Cashfree redirects here after the customer completes (or abandons)
 * checkout — CRITICALLY, we do NOT trust the redirect alone. This page
 * polls GET /payments/orders/{id} until the webhook flips the order to
 * successful / failed (the webhook is the sole source of truth).
 */
export default function BillingReturn() {
    const [params] = useSearchParams();
    const orderId = params.get("order_id") || "";
    const nav = useNavigate();
    const [order, setOrder] = useState(null);
    const [ticks, setTicks] = useState(0);
    const [expired, setExpired] = useState(false);

    const refresh = useCallback(async () => {
        try {
            const r = await api.get(`/payments/orders/${orderId}`);
            setOrder(r.data);
            return r.data;
        } catch (err) {
            toast.error(errMsg(err, "Order not found"));
            nav("/doctor/packages");
            return null;
        }
    }, [orderId, nav]);

    useEffect(() => {
        if (!orderId) return;
        let done = false;
        (async () => {
            for (let i = 0; i < 30; i += 1) {   // ~90s at 3s interval
                if (done) return;
                const o = await refresh();
                if (o?.status === "successful" || o?.status === "failed") return;
                setTicks(i + 1);
                await new Promise((r) => setTimeout(r, 3000));
            }
            setExpired(true);
        })();
        return () => { done = true; };
    }, [orderId, refresh]);

    const final = order?.status === "successful" || order?.status === "failed";

    return (
        <DoctorShell>
            <div className="max-w-2xl mx-auto py-12 px-4">
                <h1 className="text-2xl font-bold text-[#123B5D] mb-2">Payment status</h1>
                <p className="text-sm text-[#17212B]/60 mb-6">
                    Order <span className="font-mono">{orderId}</span>
                </p>

                {order ? (
                    <div className="ddi-card p-6" data-testid="billing-return-card">
                        {order.status === "successful" ? (
                            <div className="text-[#15803D] flex items-center gap-3" data-testid="return-status-success">
                                <CheckCircle2 size={22} />
                                <div>
                                    <div className="font-bold">Payment successful — {inr(order.total_inr)}</div>
                                    <div className="text-xs text-[#17212B]/70 mt-1">Invoice has been generated. You&rsquo;ll receive a copy in Billing.</div>
                                </div>
                            </div>
                        ) : order.status === "failed" ? (
                            <div className="text-[#DC2626] flex items-center gap-3" data-testid="return-status-failed">
                                <XCircle size={22} />
                                <div>
                                    <div className="font-bold">Payment failed</div>
                                    <div className="text-xs text-[#17212B]/70 mt-1">No charge was made. You can retry from your packages page.</div>
                                </div>
                            </div>
                        ) : expired ? (
                            <div className="text-[#D97706] flex items-center gap-3" data-testid="return-status-pending">
                                <AlertTriangle size={22} />
                                <div>
                                    <div className="font-bold">Verifying — still awaiting confirmation</div>
                                    <div className="text-xs text-[#17212B]/70 mt-1">
                                        The bank hasn&rsquo;t confirmed yet. Check Billing in a few minutes — no need to pay again. If the amount was debited but not credited within 24 hours, contact support: +91 7722090290.
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="text-[#123B5D] flex items-center gap-3" data-testid="return-status-verifying">
                                <Loader2 size={22} className="animate-spin" />
                                <div>
                                    <div className="font-bold">Verifying payment&hellip;</div>
                                    <div className="text-xs text-[#17212B]/70 mt-1">
                                        Waiting for Cashfree webhook confirmation ({ticks * 3}s / ~90s). Do not close this tab.
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="ddi-card p-6 text-[#17212B]/60 flex items-center gap-2">
                        <Loader2 size={18} className="animate-spin" /> Loading order&hellip;
                    </div>
                )}

                <div className="mt-6 flex gap-3">
                    <Link to="/doctor/billing" className="text-sm text-[#0F766E] font-semibold hover:underline">
                        Go to Billing →
                    </Link>
                    {final ? (
                        <Link to="/doctor" className="text-sm text-[#123B5D] font-semibold hover:underline ml-auto">
                            Back to dashboard →
                        </Link>
                    ) : null}
                </div>
            </div>
        </DoctorShell>
    );
}
